import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hrms, it, claims } from '../../services/api';
import { theme } from '../../theme';

// ─── Module-level: only tracks open state across re-mounts, not history ───────
let _wasOpen = false;

// ─── Constants ────────────────────────────────────────────────────────────────
const MENU_OPTIONS = [
  { id: 'leave',  label: 'Leave Request',  icon: '🏖️', desc: 'Check your leave request status' },
  { id: 'wfh',    label: 'WFH Request',    icon: '🏠', desc: 'Check your WFH request status' },
  { id: 'ticket', label: 'IT Ticket',      icon: '🎫', desc: 'Check your IT ticket status' },
  { id: 'claim',  label: 'Expense Claim',  icon: '💰', desc: 'Check your claim status' },
];

const STATUS_STYLES = {
  pending:      { bg: '#FEF3C7', color: '#D97706', label: 'Pending' },
  approved:     { bg: '#D1FAE5', color: '#059669', label: 'Approved' },
  rejected:     { bg: '#FEE2E2', color: '#DC2626', label: 'Rejected' },
  cancelled:    { bg: '#F3F4F6', color: '#6B7280', label: 'Cancelled' },
  open:         { bg: '#DBEAFE', color: '#2563EB', label: 'Open' },
  in_progress:  { bg: '#EDE9FE', color: '#7C3AED', label: 'In Progress' },
  resolved:     { bg: '#D1FAE5', color: '#059669', label: 'Resolved' },
  closed:       { bg: '#F3F4F6', color: '#6B7280', label: 'Closed' },
  submitted:    { bg: '#DBEAFE', color: '#2563EB', label: 'Submitted' },
  under_review: { bg: '#EDE9FE', color: '#7C3AED', label: 'Under Review' },
  verified:     { bg: '#FEF3C7', color: '#D97706', label: 'Verified' },
  paid:         { bg: '#D1FAE5', color: '#059669', label: 'Paid' },
};

const PRIORITY_STYLES = {
  low:      { color: '#6B7280', label: 'Low' },
  medium:   { color: '#D97706', label: 'Medium' },
  high:     { color: '#DC2626', label: 'High' },
  critical: { color: '#7C3AED', label: 'Critical' },
};

// ─── Small UI helpers ─────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#F3F4F6', color: '#6B7280', label: status || '—' };
  return (
    <span style={{ display:'inline-block', padding:'0.2rem 0.65rem', background:s.bg, color:s.color,
      borderRadius:999, fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.03em', whiteSpace:'nowrap' }}>
      {s.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
      padding:'0.35rem 0', borderBottom:'1px solid #F3F4F6' }}>
      <span style={{ fontSize:'0.75rem', color:'#9CA3AF', fontWeight:500, flexShrink:0, marginRight:8 }}>{label}</span>
      <span style={{ fontSize:'0.78rem', color:theme.text, fontWeight:600, textAlign:'right' }}>{value}</span>
    </div>
  );
}

// ─── Message renderers (data → JSX, callbacks always fresh) ──────────────────
function BotContent({ msg, onMenuSelect, onLeaveSelect, onWFHSelect, onTicketSelect, onClaimSelect, onBack }) {
  const { kind, data } = msg;

  if (kind === 'welcome') {
    return (
      <div>
        <p style={{ margin:'0 0 0.5rem', fontWeight:700, fontSize:'0.9rem' }}>
          👋 Hi {data.name}! I'm your Bonton Assistant.
        </p>
        <p style={{ margin:'0 0 0.9rem', fontSize:'0.82rem', color:'#6B7280' }}>
          I can help you check the status of your requests. What would you like to track today?
        </p>
        <MenuOptions onSelect={onMenuSelect} />
      </div>
    );
  }

  if (kind === 'menu') {
    return (
      <div>
        <p style={{ margin:'0 0 0.75rem', fontSize:'0.85rem' }}>Sure! What would you like to check?</p>
        <MenuOptions onSelect={onMenuSelect} />
      </div>
    );
  }

  if (kind === 'leave-list') {
    const items = data || [];
    if (!items.length) return <EmptyState icon="🏖️" text="You have no leave requests yet." onBack={onBack} />;
    return (
      <div>
        <p style={{ margin:'0 0 0.65rem', fontSize:'0.82rem', color:'#6B7280' }}>
          Found {items.length} leave request{items.length !== 1 ? 's' : ''}. Select one to see details:
        </p>
        <ListContainer>
          {items.map(item => (
            <ListItem key={item.id} onClick={() => onLeaveSelect(item)}
              title={item.leave_type_name || 'Leave'}
              sub={`${fmtDate(item.from_date)} → ${fmtDate(item.to_date)} · ${item.days} day${item.days !== 1 ? 's' : ''}`}
              right={<StatusBadge status={item.status} />}
            />
          ))}
        </ListContainer>
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'leave-detail') {
    const item = data;
    return (
      <div>
        <DetailHeader title={item.leave_type_name} status={item.status} />
        <DetailRow label="From"     value={fmtDate(item.from_date)} />
        <DetailRow label="To"       value={fmtDate(item.to_date)} />
        <DetailRow label="Duration" value={`${item.days} day${item.days !== 1 ? 's' : ''}`} />
        <DetailRow label="Reason"   value={item.reason} />
        {item.rejection_reason && <DetailRow label="Rejection Note" value={item.rejection_reason} />}
        <DetailRow label="Applied"  value={fmtDate(item.created_at)} />
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'wfh-list') {
    const items = data || [];
    if (!items.length) return <EmptyState icon="🏠" text="You have no WFH requests yet." onBack={onBack} />;
    return (
      <div>
        <p style={{ margin:'0 0 0.65rem', fontSize:'0.82rem', color:'#6B7280' }}>
          Found {items.length} WFH request{items.length !== 1 ? 's' : ''}. Select one to see details:
        </p>
        <ListContainer>
          {items.map(item => (
            <ListItem key={item.id} onClick={() => onWFHSelect(item)}
              title={`WFH — ${fmtDate(item.date)}`}
              sub={`Applied ${fmtDate(item.created_at)}`}
              right={<StatusBadge status={item.status?.value || item.status} />}
            />
          ))}
        </ListContainer>
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'wfh-detail') {
    const item = data;
    const status = item.status?.value || item.status;
    return (
      <div>
        <DetailHeader title="WFH Request" status={status} />
        <DetailRow label="Date"           value={fmtDate(item.date)} />
        <DetailRow label="Reason"         value={item.reason} />
        {item.rejection_reason && <DetailRow label="Rejection Note" value={item.rejection_reason} />}
        {item.approved_at && <DetailRow label="Approved On" value={fmtDate(item.approved_at)} />}
        <DetailRow label="Applied"        value={fmtDate(item.created_at)} />
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'ticket-list') {
    const items = data || [];
    if (!items.length) return <EmptyState icon="🎫" text="You have no IT tickets yet." onBack={onBack} />;
    return (
      <div>
        <p style={{ margin:'0 0 0.65rem', fontSize:'0.82rem', color:'#6B7280' }}>
          Found {items.length} ticket{items.length !== 1 ? 's' : ''}. Select one to see details:
        </p>
        <ListContainer>
          {items.map(item => {
            const pri = PRIORITY_STYLES[item.priority?.value || item.priority];
            return (
              <ListItem key={item.id} onClick={() => onTicketSelect(item)}
                title={`${item.ticket_code} · ${item.title}`}
                sub={
                  <span style={{ display:'flex', gap:8 }}>
                    <span>{item.category?.value || item.category}</span>
                    {pri && <span style={{ color:pri.color, fontWeight:700 }}>● {pri.label}</span>}
                  </span>
                }
                right={<StatusBadge status={item.status?.value || item.status} />}
              />
            );
          })}
        </ListContainer>
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'ticket-detail') {
    const item = data;
    return (
      <div>
        <DetailHeader title={item.title} status={item.status?.value || item.status} />
        <DetailRow label="Ticket ID"   value={item.ticket_code} />
        <DetailRow label="Category"    value={item.category?.value || item.category} />
        <DetailRow label="Priority"    value={item.priority?.value || item.priority} />
        <DetailRow label="Description" value={item.description} />
        {item.resolution_notes && <DetailRow label="Resolution" value={item.resolution_notes} />}
        {item.resolved_at && <DetailRow label="Resolved On" value={fmtDate(item.resolved_at)} />}
        <DetailRow label="Raised On"   value={fmtDate(item.created_at)} />
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'claim-list') {
    const items = data || [];
    if (!items.length) return <EmptyState icon="💰" text="You have no expense claims yet." onBack={onBack} />;
    return (
      <div>
        <p style={{ margin:'0 0 0.65rem', fontSize:'0.82rem', color:'#6B7280' }}>
          Found {items.length} claim{items.length !== 1 ? 's' : ''}. Select one to see details:
        </p>
        <ListContainer>
          {items.map(item => (
            <ListItem key={item.id} onClick={() => onClaimSelect(item)}
              title={`${item.claim_code} · ${item.title}`}
              sub={`₹${Number(item.total_amount||0).toLocaleString('en-IN')}${item.project_name ? ` · ${item.project_name}` : ''}`}
              right={<StatusBadge status={item.status} />}
            />
          ))}
        </ListContainer>
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'claim-detail') {
    const item = data;
    return (
      <div>
        <DetailHeader title={item.title} status={item.status} />
        <DetailRow label="Claim ID"    value={item.claim_code} />
        <DetailRow label="Amount"      value={`₹${Number(item.total_amount||0).toLocaleString('en-IN')}`} />
        <DetailRow label="Project"     value={item.project_name} />
        <DetailRow label="Description" value={item.description} />
        {item.finance_remarks && <DetailRow label="Finance Remarks" value={item.finance_remarks} />}
        {item.reviewed_at && <DetailRow label="Reviewed On" value={fmtDate(item.reviewed_at)} />}
        <DetailRow label="Submitted"   value={fmtDate(item.submitted_at)} />
        <BackButton onBack={onBack} />
      </div>
    );
  }

  if (kind === 'error') {
    return (
      <div>
        <p style={{ margin:'0 0 0.5rem', fontSize:'0.85rem', color:'#DC2626' }}>
          ⚠️ Something went wrong while fetching your data. Please try again.
        </p>
        <BackButton onBack={onBack} />
      </div>
    );
  }

  return null;
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function MenuOptions({ onSelect }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
      {MENU_OPTIONS.map(opt => (
        <button key={opt.id} onClick={() => onSelect(opt)}
          style={{ display:'flex', alignItems:'center', gap:'0.65rem', padding:'0.6rem 0.85rem',
            background:'#F8FAFC', border:`1.5px solid #E5E7EB`, borderRadius:'0.75rem',
            cursor:'pointer', textAlign:'left', width:'100%',
            transition:'border-color 0.15s, background 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor=theme.primary; e.currentTarget.style.background='#FFF7ED'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='#E5E7EB'; e.currentTarget.style.background='#F8FAFC'; }}
        >
          <span style={{ fontSize:'1.1rem', flexShrink:0 }}>{opt.icon}</span>
          <div>
            <p style={{ margin:0, fontWeight:600, fontSize:'0.82rem', color:theme.text }}>{opt.label}</p>
            <p style={{ margin:0, fontSize:'0.72rem', color:'#9CA3AF' }}>{opt.desc}</p>
          </div>
          <span style={{ marginLeft:'auto', color:'#D1D5DB', fontSize:'0.9rem' }}>›</span>
        </button>
      ))}
    </div>
  );
}

function ListContainer({ children }) {
  return <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>{children}</div>;
}

function ListItem({ onClick, title, sub, right }) {
  return (
    <button onClick={onClick}
      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'0.6rem 0.75rem', background:'#F8FAFC',
        border:'1.5px solid #E5E7EB', borderRadius:'0.65rem',
        cursor:'pointer', textAlign:'left', width:'100%',
        transition:'border-color 0.15s, background 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor=theme.primary; e.currentTarget.style.background='#FFF7ED'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor='#E5E7EB'; e.currentTarget.style.background='#F8FAFC'; }}
    >
      <div style={{ flex:1, minWidth:0, marginRight:8 }}>
        <p style={{ margin:0, fontWeight:600, fontSize:'0.8rem', color:theme.text,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</p>
        <p style={{ margin:'0.1rem 0 0', fontSize:'0.72rem', color:'#9CA3AF' }}>{sub}</p>
      </div>
      {right}
    </button>
  );
}

function DetailHeader({ title, status }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
      marginBottom:'0.6rem', gap:8 }}>
      <p style={{ margin:0, fontWeight:700, fontSize:'0.88rem', lineHeight:1.3 }}>{title}</p>
      <StatusBadge status={status} />
    </div>
  );
}

function BackButton({ onBack }) {
  return (
    <button onClick={onBack}
      style={{ marginTop:'0.75rem', display:'flex', alignItems:'center', gap:4,
        background:'none', border:'none', cursor:'pointer',
        color:theme.primary, fontSize:'0.78rem', fontWeight:600, padding:0 }}>
      ← Back to menu
    </button>
  );
}

function EmptyState({ icon, text, onBack }) {
  return (
    <div>
      <div style={{ textAlign:'center', padding:'0.75rem 0' }}>
        <p style={{ fontSize:'1.6rem', margin:'0 0 0.4rem' }}>{icon}</p>
        <p style={{ margin:0, fontSize:'0.82rem', color:'#6B7280' }}>{text}</p>
      </div>
      <BackButton onBack={onBack} />
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ChatBot() {
  const { user } = useAuth();
  const [open, setOpen]         = useState(_wasOpen);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [pulse, setPulse]       = useState(true);
  const bodyRef = useRef(null);

  // ── Drag state ──────────────────────────────────────────────────────────────
  const [pos, setPos] = useState(() => ({
    x: window.innerWidth  - 86,
    y: window.innerHeight - 86,
  }));
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    drag.current = { active: true, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y, moved: false };
    setDragging(true);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!drag.current.active) return;
      const dx = e.clientX - drag.current.startX;
      const dy = e.clientY - drag.current.startY;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) drag.current.moved = true;
      const BTN = 58;
      setPos({
        x: Math.max(8, Math.min(window.innerWidth  - BTN - 8, drag.current.origX + dx)),
        y: Math.max(8, Math.min(window.innerHeight - BTN - 8, drag.current.origY + dy)),
      });
    };
    const onUp = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, []);

  const handleBubbleClick = () => {
    if (drag.current.moved) { drag.current.moved = false; return; }
    if (open) { setMessages([]); setPulse(true); }
    setOpen(o => !o);
  };
  // ── End drag state ──────────────────────────────────────────────────────────

  // Sync open state across re-mounts
  useEffect(() => { _wasOpen = open; }, [open]);

  // Auto-scroll
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  // Stop pulse after first open
  useEffect(() => { if (open) setPulse(false); }, [open]);

  // Show welcome only on very first open
  useEffect(() => {
    if (open && messages.length === 0) {
      const name = user?.full_name?.split(' ')[0] || 'there';
      pushBot('welcome', { name });
    }
  }, [open]); // eslint-disable-line

  function pushBot(kind, data = null) {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), type: 'bot', kind, data }]);
  }

  function pushUser(text) {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), type: 'user', kind: 'text', data: text }]);
  }

  function showMenu() {
    pushBot('menu');
  }

  async function handleMenuSelect(option) {
    pushUser(option.label);
    setLoading(true);
    try {
      if (option.id === 'leave') {
        const r = await hrms.getMyLeaveRequests();
        pushBot('leave-list', r.data || []);
      } else if (option.id === 'wfh') {
        const r = await hrms.getMyWFHRequests();
        pushBot('wfh-list', r.data || []);
      } else if (option.id === 'ticket') {
        const r = await it.getMyTickets();
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        pushBot('ticket-list', items);
      } else if (option.id === 'claim') {
        const r = await claims.getMyClaims();
        pushBot('claim-list', r.data || []);
      }
    } catch {
      pushBot('error');
    } finally {
      setLoading(false);
    }
  }

  function handleLeaveSelect(item)  { pushUser(`${item.leave_type_name} — ${fmtDate(item.from_date)}`); pushBot('leave-detail', item); }
  function handleWFHSelect(item)    { pushUser(`WFH — ${fmtDate(item.date)}`); pushBot('wfh-detail', item); }
  function handleTicketSelect(item) { pushUser(`${item.ticket_code} — ${item.title}`); pushBot('ticket-detail', item); }
  function handleClaimSelect(item)  { pushUser(`${item.claim_code} — ${item.title}`); pushBot('claim-detail', item); }

  const callbacks = {
    onMenuSelect:   handleMenuSelect,
    onLeaveSelect:  handleLeaveSelect,
    onWFHSelect:    handleWFHSelect,
    onTicketSelect: handleTicketSelect,
    onClaimSelect:  handleClaimSelect,
    onBack:         showMenu,
  };

  return (
    <>
      {/* Draggable wrapper — positions both bubble and panel */}
      <div
        style={{
          position: 'fixed', left: pos.x, top: pos.y, zIndex: 1000,
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Chat panel — always mounted, positioned above the bubble */}
        <div style={{
          position:'absolute', bottom:66, right:0,
          width:380, maxHeight:'78vh',
          background:'#fff', borderRadius:'1.25rem',
          boxShadow:'0 24px 64px rgba(0,0,0,0.16), 0 8px 24px rgba(0,0,0,0.08)',
          display:'flex', flexDirection:'column', overflow:'hidden',
          opacity: open ? 1 : 0,
          pointerEvents: open && !dragging ? 'auto' : 'none',
          transform: open ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
          transition:'opacity 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        }}
          onMouseDown={e => e.stopPropagation()}
        >
        {/* Header */}
        <div style={{
          background:`linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          padding:'1rem 1.25rem', display:'flex', alignItems:'center', gap:'0.75rem',
        }}>
          <div style={{
            width:42, height:42, borderRadius:'50%',
            background:'rgba(255,255,255,0.2)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'1.3rem', flexShrink:0, border:'2px solid rgba(255,255,255,0.35)',
          }}>🤖</div>
          <div style={{ flex:1 }}>
            <p style={{ margin:0, fontWeight:700, color:'#fff', fontSize:'0.95rem' }}>Bonton Assistant</p>
            <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:2 }}>
              <span style={{ width:7, height:7, borderRadius:'50%', background:'#4ADE80', display:'inline-block' }} />
              <span style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.85)' }}>Online · Always here to help</span>
            </div>
          </div>
          <button onClick={() => { setMessages([]); setPulse(true); setOpen(false); }} style={{
            background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'50%',
            width:30, height:30, cursor:'pointer', color:'#fff',
            fontSize:'0.85rem', display:'flex', alignItems:'center', justifyContent:'center',
          }}>✕</button>
        </div>

        {/* Messages */}
        <div ref={bodyRef} style={{
          flex:1, overflowY:'auto', padding:'1rem',
          display:'flex', flexDirection:'column', gap:'0.75rem',
          background:'#F8FAFC',
        }}>
          {messages.map(msg => (
            <div key={msg.id} style={{ display:'flex', justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start' }}>
              {msg.type === 'bot' && (
                <div style={{
                  width:28, height:28, borderRadius:'50%', flexShrink:0,
                  background:`linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'0.75rem', marginRight:8, marginTop:2,
                }}>🤖</div>
              )}
              <div style={{
                maxWidth: msg.type === 'user' ? '72%' : '88%',
                background: msg.type === 'user'
                  ? `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`
                  : '#fff',
                color: msg.type === 'user' ? '#fff' : theme.text,
                borderRadius: msg.type === 'user' ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                padding: msg.type === 'user' ? '0.55rem 0.9rem' : '0.75rem 0.9rem',
                fontSize:'0.85rem',
                boxShadow: msg.type === 'user'
                  ? '0 2px 8px rgba(249,115,22,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.06)',
                lineHeight:1.5,
              }}>
                {msg.type === 'user'
                  ? msg.data
                  : <BotContent msg={msg} {...callbacks} />
                }
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{
                width:28, height:28, borderRadius:'50%',
                background:`linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem',
              }}>🤖</div>
              <div style={{
                background:'#fff', borderRadius:'1rem 1rem 1rem 0.25rem',
                padding:'0.7rem 1rem', boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
                display:'flex', gap:5, alignItems:'center',
              }}>
                {[0,1,2].map(i => (
                  <span key={i} style={{
                    width:7, height:7, borderRadius:'50%', background:theme.primary,
                    display:'inline-block',
                    animation:`chatDot 1.2s ease-in-out ${i*0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding:'0.6rem 1rem', borderTop:`1px solid ${theme.border}`,
          background:'#fff', fontSize:'0.7rem', color:theme.textMuted, textAlign:'center',
        }}>
          Bonton Assistant · Powered by BonTon HRMS
        </div>
        </div>{/* end panel */}

        {/* Floating bubble */}
        <button
          onClick={handleBubbleClick}
          title="Bonton Assistant"
          style={{
            position:'relative',
            width:58, height:58, borderRadius:'50%',
            background:`linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
            border:'none', cursor: dragging ? 'grabbing' : 'grab',
            boxShadow:'0 4px 20px rgba(249,115,22,0.45), 0 2px 8px rgba(0,0,0,0.15)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize: open ? '1.3rem' : '1.5rem',
            transition:'transform 0.2s ease, box-shadow 0.2s ease',
            transform: open ? 'rotate(10deg) scale(0.95)' : 'scale(1)',
          }}
        >
          {open ? '✕' : '💬'}
          {pulse && !open && (
            <span style={{
              position:'absolute', top:4, right:4, width:12, height:12,
              borderRadius:'50%', background:'#10B981', border:'2px solid #fff',
              animation:'chatPulse 1.8s ease-in-out infinite',
            }} />
          )}
        </button>
      </div>{/* end draggable wrapper */}

      <style>{`
        @keyframes chatDot {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40%            { transform: scale(1);   opacity: 1; }
        }
        @keyframes chatPulse {
          0%, 100% { transform: scale(1);   opacity: 1; }
          50%      { transform: scale(1.5); opacity: 0.5; }
        }
      `}</style>
    </>
  );
}
